import React, { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { DaySelection } from '../../hooks/useDataWindow';
import { formatDayLabel, formatShortDay } from '../../utils/dateWindow';
import type { IsoDay } from '../../utils/dateWindow';
import { MAX_VISIBLE_DAYS, calendarAxis, tooltipLeftPct, visibleWindow } from '../../utils/deliveryBars';
import { pluralize } from '../../utils/plural';

export interface DeliveryDayBarsProps {
  /** Delivered days, ascending (dw.dates or coverage dates). */
  dates: readonly IsoDay[];
  /** Accessible name: "Delivery day" in the picker; "<Product> in <Country>, delivery days" on Home. */
  label: string;
  mode: 'select' | 'view';
  selection?: DaySelection | null;
  onSelect?(day: IsoDay, opts: { extend: boolean }): void;
  /** Shift gestures pass extend: true only when this is true. */
  rangeEnabled?: boolean;
  maxDays?: number;
  className?: string;
}

const BAR_BASE =
  'relative min-w-[2px] max-w-4 flex-1 rounded-[2px] focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-note';
const BAR_DELIVERED = 'bg-ok hover:bg-ok/80';
const BAR_GREY = 'bg-paper-300 hover:bg-paper-400 cursor-default';
const BAR_SELECTED = 'z-10 outline outline-2 outline-offset-1 outline-ink';

/**
 * One bar per calendar day from the first delivered day to the latest: green when something was
 * delivered, grey when not. In select mode the delivered bars are buttons and the whole strip is one
 * tab stop; in view mode it is a read-only list. At most `maxDays` bars show at once.
 */
const DeliveryDayBars: React.FC<DeliveryDayBarsProps> = ({
  dates,
  label,
  mode,
  selection = null,
  onSelect,
  rangeEnabled = false,
  maxDays = MAX_VISIBLE_DAYS,
  className,
}) => {
  const id = useId();
  const select = mode === 'select';
  const axis = useMemo(() => calendarAxis(dates), [dates]);
  const indexOf = useMemo(() => new Map(axis.map((b, i) => [b.day, i])), [axis]);

  const [paging, setPaging] = useState<{ end: number; forEnd: IsoDay | null } | null>(null);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [tooltipHidden, setTooltipHidden] = useState(false);
  const [leftPct, setLeftPct] = useState(0);
  const barRefs = useRef<Array<HTMLElement | null>>([]);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const tipRef = useRef<HTMLSpanElement | null>(null);
  const swallowSpace = useRef(false);

  // Paging only holds while the selection it was made for is still the selection.
  const requestedEnd = paging && paging.forEnd === (selection?.end ?? null) ? paging.end : null;
  const include = selection ? (indexOf.get(selection.end) ?? null) : null;
  const view = visibleWindow(axis.length, maxDays, requestedEnd, include);
  const visible = axis.slice(view.start, view.end);

  const tipIndex = hoverIndex ?? focusIndex;
  const showTip = !tooltipHidden && tipIndex !== null && tipIndex >= view.start && tipIndex < view.end;

  useLayoutEffect(() => {
    if (!showTip || tipIndex === null) return;
    const wrap = wrapRef.current;
    const barEl = barRefs.current[tipIndex];
    const tip = tipRef.current;
    if (!wrap || !barEl || !tip) return;
    const w = wrap.getBoundingClientRect();
    const b = barEl.getBoundingClientRect();
    setLeftPct(tooltipLeftPct(b.left + b.width / 2 - w.left, w.width, tip.offsetWidth));
  }, [tipIndex, showTip, view.start, view.end]);

  if (axis.length === 0) return null;

  const inView = (i: number | null): i is number => i !== null && i >= view.start && i < view.end;
  const selectionStart = selection ? (indexOf.get(selection.start) ?? null) : null;
  let lastDelivered = -1;
  visible.forEach((b, k) => {
    if (b.delivered) lastDelivered = view.start + k;
  });
  const stop = inView(focusIndex)
    ? focusIndex
    : inView(selectionStart)
      ? selectionStart
      : lastDelivered >= 0
        ? lastDelivered
        : view.start;

  const inSelection = (day: IsoDay) => !!selection && day >= selection.start && day <= selection.end;

  const moveTo = (i: number) => {
    const target = Math.max(view.start, Math.min(view.end - 1, i));
    barRefs.current[target]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const index = Number((e.target as HTMLElement).dataset.index);
    if (!Number.isInteger(index)) return;
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        moveTo(index - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        moveTo(index + 1);
        break;
      case 'Home':
        e.preventDefault();
        moveTo(view.start);
        break;
      case 'End':
        e.preventDefault();
        moveTo(view.end - 1);
        break;
      case 'Escape':
        setTooltipHidden(true);
        break;
      case 'Enter':
      case ' ':
        if (e.shiftKey) {
          // Shift+Enter and Shift+Space act like Shift+click.
          e.preventDefault();
          if (e.key === ' ') swallowSpace.current = true;
          const bar = axis[index];
          if (select && bar?.delivered) onSelect?.(bar.day, { extend: !!rangeEnabled });
        }
        break;
      default:
    }
  };

  // Stops the browser firing a second click when Shift+Space is released.
  const onKeyUp = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === ' ' && (swallowSpace.current || e.shiftKey)) {
      e.preventDefault();
      // The browser's click for this key press, if any, arrives in the same task; clear after it.
      setTimeout(() => {
        swallowSpace.current = false;
      }, 0);
    }
  };

  const deliveredCount = visible.filter((b) => b.delivered).length;
  const firstDay = visible[0].day;
  const lastDay = visible[visible.length - 1].day;
  const sameYear = firstDay.slice(0, 4) === lastDay.slice(0, 4);

  const earlierButton = view.hasEarlier ? (
    <button
      type="button"
      className="btn btn-ghost btn-xs"
      aria-label="Show earlier days"
      onClick={() => setPaging({ end: view.start, forEnd: selection?.end ?? null })}
    >
      ‹ Earlier
    </button>
  ) : null;
  const laterButton = view.hasLater ? (
    <button
      type="button"
      className="btn btn-ghost btn-xs"
      aria-label="Show later days"
      onClick={() => setPaging({ end: Math.min(axis.length, view.end + maxDays), forEnd: selection?.end ?? null })}
    >
      Later ›
    </button>
  ) : null;

  const tooltip = () => {
    if (!showTip || tipIndex === null) return null;
    const bar = axis[tipIndex];
    const state = bar.delivered ? 'Delivered' : 'Not delivered';
    return (
      <span
        ref={tipRef}
        data-testid="day-tooltip"
        aria-hidden="true"
        style={{ left: `${leftPct}%` }}
        className="pointer-events-none absolute bottom-full z-20 mb-1 block whitespace-nowrap rounded bg-ink px-2 py-1 text-left text-xs font-normal text-surface shadow"
      >
        <span className="block">{formatDayLabel(bar.day)}</span>
        <span className="block">{select && inSelection(bar.day) ? `${state} · Selected` : state}</span>
      </span>
    );
  };

  const common = (i: number) => ({
    ref: (el: HTMLElement | null) => {
      barRefs.current[i] = el;
    },
    'data-index': i,
    tabIndex: i === stop ? 0 : -1,
    onFocus: () => {
      setFocusIndex(i);
      setTooltipHidden(false);
    },
    onBlur: () => setFocusIndex((cur) => (cur === i ? null : cur)),
    onMouseEnter: () => {
      setHoverIndex(i);
      setTooltipHidden(false);
    },
  });

  return (
    <div className={className}>
      {select ? (
        <>
          <span id={`${id}-yes`} hidden>
            Delivered
          </span>
          <span id={`${id}-no`} hidden>
            Not delivered
          </span>
        </>
      ) : null}
      <div className="relative" ref={wrapRef}>
        {tooltip()}
        <div
          role={select ? 'group' : 'list'}
          aria-label={label}
          className="flex h-8 items-stretch gap-px"
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onMouseLeave={() => setHoverIndex(null)}
        >
          {visible.map((bar, k) => {
            const i = view.start + k;
            const selected = inSelection(bar.day);
            const className = [
              BAR_BASE,
              bar.delivered ? BAR_DELIVERED : BAR_GREY,
              select && bar.delivered ? 'cursor-pointer' : '',
              select && selected ? BAR_SELECTED : '',
            ]
              .filter(Boolean)
              .join(' ');
            if (!select) {
              return (
                <span
                  key={bar.day}
                  role="listitem"
                  className={className}
                  aria-label={`${formatDayLabel(bar.day)}, ${bar.delivered ? 'delivered' : 'not delivered'}`}
                  {...common(i)}
                />
              );
            }
            return bar.delivered ? (
              <button
                key={bar.day}
                type="button"
                className={className}
                aria-label={formatDayLabel(bar.day)}
                aria-pressed={selected}
                aria-describedby={`${id}-yes`}
                onClick={(e) => {
                  if (swallowSpace.current) {
                    swallowSpace.current = false;
                    return;
                  }
                  onSelect?.(bar.day, { extend: !!rangeEnabled && e.shiftKey });
                }}
                {...common(i)}
              />
            ) : (
              <button
                key={bar.day}
                type="button"
                className={className}
                aria-label={formatDayLabel(bar.day)}
                aria-disabled="true"
                aria-describedby={`${id}-no`}
                {...common(i)}
              />
            );
          })}
        </div>
      </div>
      <div className="mt-1 flex items-center justify-between gap-2 text-xs text-ink-subtle">
        <span className="flex items-center gap-1">
          {earlierButton}
          <span>{formatShortDay(firstDay, !sameYear)}</span>
        </span>
        <span className="flex items-center gap-1">
          <span>{formatShortDay(lastDay, true)}</span>
          {laterButton}
        </span>
      </div>
      <p className="mt-0.5 text-xs text-ink-muted">
        {deliveredCount} of {pluralize(visible.length, 'day')} delivered
      </p>
    </div>
  );
};

export default DeliveryDayBars;
